<script lang="ts" module>
  import type { CartLine, CartLineCustomStickerContent } from '@stickerapp-org/checkout-sdk';

  interface PreviewAttributes {
    material: string;
    laminate: string;
    width_mm: string;
    height_mm: string;
    note?: string;
    icon?: string;
    figureId?: string;
  }

  export interface PreviewSticker extends CartLine {
    content: CartLineCustomStickerContent & { attributes: PreviewAttributes };
  }

  export const isPreviewSticker = (item: CartLine): item is PreviewSticker =>
    item.content.type === 'customSticker';
</script>

<script lang="ts">
  import { Modal } from '@athena-lib/Modal';
  import { Text } from '@athena-lib/Text';
  import { formatSize } from '@athena-lib/ProductWizard';
  import type { LocalizationContext } from '@athena-lib/i18n';
  import type { CheckoutSdk, createCurrencyFormatter } from '@athena-lib/Checkout/utils';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { LiveLineItemStickerPreview } from '@athena-lib/LivePreview';
  import { Image } from '@athena-lib/Image';
  import { getProductsTranslators } from '$lib/checkout.svelte';

  type Props = {
    item: PreviewSticker;
    locale: LocalizationContext;
    onClose: () => void;
    formatCurrency: ReturnType<typeof createCurrencyFormatter>;
    getCustomSticker: CheckoutSdk['getCustomSticker'];
  };

  const { item, locale, onClose, formatCurrency, getCustomSticker }: Props = $props();
  const sides: Array<'back' | 'front'> = $derived.by(() => {
    const feature = item.content.attributes.feature;
    const hasBackPaperPrint = Array.isArray(feature) ? feature.includes('backpaper_print') : false;
    return hasBackPaperPrint ? ['front', 'back'] : ['front'];
  });
  let activePart = $state<'back' | 'front'>('front');

  const translations = getCommonTranslations();
  const atlasTranslations = getProductsTranslators();
  const getMaterialTranslation = (key: string) => atlasTranslations?.translateMaterial(key) || key;
  const getLaminateTranslation = (key: string) => atlasTranslations?.translateLaminate(key) || key;

  const imageUrl = $derived(item.content.imageUrl);
  const formattedSize = $derived(
    formatSize(item.content.attributes.width_mm, item.content.attributes.height_mm, locale, true),
  );
  const translatedName = $derived(
    translations[item.content.name as keyof typeof translations] ?? item.content.name,
  );
</script>

<Modal {onClose} closable center showModal size="none" class="w-fit">
  <div class="mt-4 flex flex-col items-center gap-8 p-8 md:mt-0 md:flex-row md:p-16">
    <div class="flex flex-col">
      <div class="checkered mt-2 flex h-80 w-80 items-center justify-center py-4 md:mt-0">
        {#if !imageUrl}
          <LiveLineItemStickerPreview
            customStickerPromise={getCustomSticker(
              item.content.customStickerId,
              item.content.customStickerVersion,
            )}
            side={activePart}
          />
        {:else}
          <Image src={imageUrl} alt={item.content.name} class="aspect-auto max-h-full max-w-full" />
        {/if}
      </div>
      {#if !imageUrl && sides.length > 1}
        <div class="mt-2 flex justify-center gap-2">
          {#each sides as side, index}
            <button
              type="button"
              class="checkered size-10 ring-st-blue-500 data-[active=true]:ring-2"
              onclick={() => {
                activePart = side;
              }}
              data-active={activePart == side}
              aria-label={`${translations.stickerLayer} ${index + 1}`}
            >
              <LiveLineItemStickerPreview
                customStickerPromise={getCustomSticker(
                  item.content.customStickerId,
                  item.content.customStickerVersion,
                )}
                {side}
              />
            </button>
          {/each}
        </div>
      {/if}
    </div>
    <div class="w-full md:w-60">
      <div class="flex items-center justify-between">
        <Text element="span" bold>{translatedName ?? item.content.customStickerId}</Text>
        <Text element="span" bold>{formatCurrency(item.price.total.major.amountInclTax)}</Text>
      </div>
      <div class="mt-1 flex items-center justify-between">
        <Text element="span" size="xsmall">{item.quantity}{translations.pcs}</Text>
        <Text element="span" size="xsmall"
          >{formatCurrency(item.price.unit.major.amountInclTax)}/{translations.pc}</Text
        >
      </div>
      {#if item.content.type == 'customSticker'}
        <div class="mt-4 flex flex-col">
          <Text element="span" size="xsmall">{formattedSize}</Text>
          <Text element="span" size="xsmall"
            >{getMaterialTranslation(item.content.attributes.material)}</Text
          >
          <Text element="span" size="xsmall"
            >{getLaminateTranslation(item.content.attributes.laminate)}</Text
          >
        </div>
      {/if}
      {#if item.content.attributes.note}
        <div class="mt-6 flex flex-col gap-2">
          <Text element="span" size="xsmall" bold>{translations.comment}:</Text>
          <Text element="p" size="xsmall">
            {item.content.attributes.note}
          </Text>
        </div>
      {/if}
    </div>
  </div>
</Modal>

<style>
  .checkered {
    --dark-tile: #dddddd;
    --light-tile: #f2f2f2;
    --size: 0.75rem;

    background: conic-gradient(
      var(--dark-tile),
      25%,
      var(--light-tile) 25%,
      var(--light-tile) 50%,
      var(--dark-tile) 50%,
      var(--dark-tile) 75%,
      var(--light-tile) 75%
    );
    background-size: calc(var(--size) * 2) calc(var(--size) * 2);
  }
</style>
